export type BillingPlanCode = 'pro-monthly' | 'pro-yearly' | 'lifetime';
export type UserPlanType = 'free' | 'pro' | 'lifetime';

export interface BillingPlanDefinition {
  /**
   * Internal identifier used throughout the app to reference the billing plan.
   */
  id: BillingPlanCode;
  /**
   * Human-friendly label shown in marketing UI.
   */
  name: string;
  /**
   * Short description used in plan cards/paywalls.
   */
  description: string;
  /**
   * Stripe price identifier for this plan.
   */
  priceId: string;
  /**
   * Stripe product identifier for documentation/reference.
   */
  productId: string;
  /**
   * Price in USD to display in UI (monthly amount for subscriptions, one-time otherwise).
   */
  amountUsd: number;
  /**
   * Billing cadence for subscriptions. `null` for one-time purchase (lifetime).
   */
  interval: 'month' | 'year' | null;
  /**
   * Stripe Checkout mode to use when purchasing this plan.
   */
  mode: 'subscription' | 'payment';
  /**
   * Maps to the plan string stored in `public.users.plan`.
   */
  planType: UserPlanType;
  /**
   * Feature bullets displayed in pricing UI.
   */
  features: string[];
  /**
   * Additional highlight/callout text for marketing cards.
   */
  highlight?: string;
}

/**
 * Canonical catalog of publicly available billing plans.
 */
export const BILLING_PLANS: Record<BillingPlanCode, BillingPlanDefinition> = {
  'pro-monthly': {
    id: 'pro-monthly',
    name: 'Pro Monthly',
    description: 'Unlimited conversions billed monthly',
    priceId: 'price_1SYBYbP1IprN7mwHuX4dpK7Y',
    productId: 'prod_TVBwk1uwhyR08R',
    amountUsd: 4.99,
    interval: 'month',
    mode: 'subscription',
    planType: 'pro',
    features: [
      'Unlimited workflow conversions',
      'Priority email & chat support',
      'All export formats + Docker',
      'Advanced workflow templates',
      'Early access to new features'
    ],
    highlight: 'Best for trying Pro month-to-month'
  },
  'pro-yearly': {
    id: 'pro-yearly',
    name: 'Pro Yearly',
    description: 'Unlimited conversions billed yearly',
    priceId: 'price_1SYBNJP1IprN7mwHmEb5VzjF',
    productId: 'prod_TVBkiNpxCGimLe',
    amountUsd: 49,
    interval: 'year',
    mode: 'subscription',
    planType: 'pro',
    features: [
      'Unlimited workflow conversions',
      'Priority email & chat support',
      'All export formats + Docker',
      'Advanced workflow templates',
      'Early access to new features'
    ],
    highlight: 'Save 32% vs monthly billing'
  },
  lifetime: {
    id: 'lifetime',
    name: 'Lifetime',
    description: 'All updates and support forever (one-time payment)',
    priceId: 'price_1SYBPJP1IprN7mwHf52wZAj2',
    productId: 'prod_TVBm3m0CQRsA3L',
    amountUsd: 400,
    interval: null,
    mode: 'payment',
    planType: 'lifetime',
    features: [
      'Unlimited workflow conversions',
      'All updates, full support forever',
      'Priority email & chat support',
      'All export formats + Docker',
      'Advanced workflow templates',
      'Early access to new features'
    ],
    highlight: 'One-time payment, no renewals ever'
  }
};

/**
 * Quick lookup from Stripe price ID to billing plan definition.
 */
export const BILLING_PLANS_BY_PRICE_ID: Record<string, BillingPlanDefinition> =
  Object.values(BILLING_PLANS).reduce<Record<string, BillingPlanDefinition>>((lookup, plan) => {
    lookup[plan.priceId] = plan;
    return lookup;
  }, {});

/**
 * Allow list of Stripe price IDs that the backend checkout endpoint should accept.
 */
export const ALLOWED_STRIPE_PRICE_IDS: string[] = Object.keys(BILLING_PLANS_BY_PRICE_ID);
